import React, { useState, useEffect, useRef } from 'react';
import { apiPost, apiGet } from '../utils/api';
import { Image, RotateCcw, Package, Crop, BarChart3, QrCode, Upload, Download, Copy, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useLocation, Link as RouterLink, Routes, Route, useNavigate } from 'react-router-dom';

// Função utilitária para obter categoria e nome da ferramenta ativa
export function getActiveToolInfo(location) {
  const tabs = [
    { id: 'convert', name: 'Conversor de Formato', path: '/images/convert' },
    { id: 'resize', name: 'Redimensionador', path: '/images/resize' },
    { id: 'compress', name: 'Compressor', path: '/images/compress' },
    { id: 'thumbnail', name: 'Gerador de Thumbnail', path: '/images/thumbnail' },
    { id: 'analyze', name: 'Analisador de Imagem', path: '/images/analyze' },
    { id: 'qr', name: 'Gerador de QR Code', path: '/images/qr' }
  ];
  const match = tabs.find(tab => location.pathname.startsWith(tab.path));
  return { category: 'Imagens', tool: match ? match.name : null };
}

const Images = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Definição das abas e rotas
  const tabs = [
    { id: 'convert', name: 'Conversor de Formato', icon: Image, path: '/images/convert', description: 'JPEG, PNG, WebP, GIF', status: 'active' },
    { id: 'resize', name: 'Redimensionador', icon: RotateCcw, path: '/images/resize', description: 'Redimensionar com proporção', status: 'active' },
    { id: 'compress', name: 'Compressor', icon: Package, path: '/images/compress', description: 'Compressão com qualidade', status: 'active' },
    { id: 'thumbnail', name: 'Gerador de Thumbnail', icon: Crop, path: '/images/thumbnail', description: 'Miniaturas personalizadas', status: 'active' },
    { id: 'analyze', name: 'Analisador de Imagem', icon: BarChart3, path: '/images/analyze', description: 'Informações detalhadas', status: 'active' },
    { id: 'qr', name: 'Gerador de QR Code', icon: QrCode, path: '/images/qr', description: 'QR codes personalizados', status: 'active' }
  ];

  // Redirecionar /images para /images/convert
  useEffect(() => {
    if (location.pathname === '/images') {
      navigate('/images/convert');
    }
  }, [location.pathname, navigate]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado para a área de transferência!');
  };

  return (
    <div className="p-6 max-w-6xl mx-auto w-full overflow-x-hidden bg-white min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Ferramentas de Imagens
        </h1>
        <p className="text-gray-600">
          Conversores e processadores de imagem
        </p>
      </div>

      {/* Navegação rápida das ferramentas */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Ferramentas Disponíveis</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {tabs.filter(tab => tab.status === 'active').map(tab => (
            <RouterLink
              key={tab.id}
              to={tab.path}
              className={`p-4 bg-white border rounded-lg transition-all duration-200 flex items-center space-x-3 ${location.pathname === tab.path ? 'border-blue-500 bg-blue-50 shadow' : 'border-gray-200 hover:shadow-md hover:border-blue-300'}`}
            >
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <tab.icon className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-gray-900 text-base md:text-[15px] lg:text-base leading-tight break-words whitespace-normal">{tab.name}</h3>
                <p className="text-sm text-gray-500 leading-snug break-words whitespace-normal">{tab.description}</p>
              </div>
            </RouterLink>
          ))}
        </div>
      </div>

      <Routes>
        <Route path="convert" element={<ImageConverter />} />
        <Route path="resize" element={<ImageResizer />} />
        <Route path="compress" element={<ImageCompressor />} />
        <Route path="thumbnail" element={<ThumbnailGenerator />} />
        <Route path="analyze" element={<ImageAnalyzer />} />
        <Route path="qr" element={<QRCodeGenerator />} />
      </Routes>
    </div>
  );
};

// Componente Conversor de Formato
const ImageConverter = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [targetFormat, setTargetFormat] = useState('jpeg');
  const [convertedImage, setConvertedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef();

  const formats = [
    { value: 'jpeg', label: 'JPEG' },
    { value: 'png', label: 'PNG' },
    { value: 'webp', label: 'WebP' },
    { value: 'gif', label: 'GIF' },
    { value: 'bmp', label: 'BMP' }
  ];

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setConvertedImage(null);
    } else {
      toast.error('Selecione um arquivo de imagem válido');
    }
  };

  const convertImage = async () => {
    if (!selectedFile) {
      toast.error('Selecione uma imagem');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('format', targetFormat);

      const data = await apiUpload('/api/images/convert-format', formData);
      setConvertedImage(data);
    } catch (error) {
      toast.error('Erro ao converter imagem');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Selecionar Imagem
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current.click()}
              className="flex items-center justify-center space-x-2 mx-auto bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <Upload size={16} />
              <span>Escolher Arquivo</span>
            </button>
            {selectedFile && (
              <div className="mt-4 text-sm text-gray-600">
                {selectedFile.name}
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Formato de Saída
          </label>
          <select
            value={targetFormat}
            onChange={(e) => setTargetFormat(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {formats.map(format => (
              <option key={format.value} value={format.value}>
                {format.label}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={convertImage}
          disabled={!selectedFile || loading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
        >
          {loading ? 'Convertendo...' : 'Converter Imagem'}
        </button>

        {convertedImage && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900">Imagem Convertida</span>
              <button
                onClick={() => window.open(convertedImage.url, '_blank')}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <Download size={16} />
              </button>
            </div>
            <img
              src={convertedImage.url}
              alt="Convertida"
              className="w-full h-auto rounded-lg"
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Redimensionador
const ImageResizer = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [width, setWidth] = useState(800);
  const [height, setHeight] = useState(600);
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [resizedImage, setResizedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef();

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setResizedImage(null);
      
      // Auto-detect dimensions
      const img = new Image();
      img.onload = () => {
        setWidth(img.width);
        setHeight(img.height);
      };
      img.src = URL.createObjectURL(file);
    } else {
      toast.error('Selecione um arquivo de imagem válido');
    }
  };

  const resizeImage = async () => {
    if (!selectedFile) {
      toast.error('Selecione uma imagem');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('width', width);
      formData.append('height', height);
      formData.append('maintainAspectRatio', maintainAspectRatio);

      const data = await apiUpload('/api/images/resize', formData);
      setResizedImage(data);
    } catch (error) {
      toast.error('Erro ao redimensionar imagem');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Selecionar Imagem
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current.click()}
              className="flex items-center justify-center space-x-2 mx-auto bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <Upload size={16} />
              <span>Escolher Arquivo</span>
            </button>
            {selectedFile && (
              <div className="mt-4 text-sm text-gray-600">
                {selectedFile.name}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Largura
            </label>
            <input
              type="number"
              value={width}
              onChange={(e) => setWidth(Number(e.target.value))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Altura
            </label>
            <input
              type="number"
              value={height}
              onChange={(e) => setHeight(Number(e.target.value))}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={maintainAspectRatio}
              onChange={(e) => setMaintainAspectRatio(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-900">
              Manter proporção
            </span>
          </label>
        </div>

        <button
          onClick={resizeImage}
          disabled={!selectedFile || loading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
        >
          {loading ? 'Redimensionando...' : 'Redimensionar Imagem'}
        </button>

        {resizedImage && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900">Imagem Redimensionada</span>
              <button
                onClick={() => window.open(resizedImage.url, '_blank')}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <Download size={16} />
              </button>
            </div>
            <img
              src={resizedImage.url}
              alt="Redimensionada"
              className="w-full h-auto rounded-lg"
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Compressor
const ImageCompressor = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [quality, setQuality] = useState(80);
  const [compressedImage, setCompressedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef();

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setCompressedImage(null);
    } else {
      toast.error('Selecione um arquivo de imagem válido');
    }
  };

  const compressImage = async () => {
    if (!selectedFile) {
      toast.error('Selecione uma imagem');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('quality', quality);

      const data = await apiUpload('/api/images/compress', formData);
      setCompressedImage(data);
    } catch (error) {
      toast.error('Erro ao comprimir imagem');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Selecionar Imagem
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current.click()}
              className="flex items-center justify-center space-x-2 mx-auto bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <Upload size={16} />
              <span>Escolher Arquivo</span>
            </button>
            {selectedFile && (
              <div className="mt-4 text-sm text-gray-600">
                {selectedFile.name}
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Qualidade: {quality}%
          </label>
          <input
            type="range"
            min="1"
            max="100"
            value={quality}
            onChange={(e) => setQuality(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        <button
          onClick={compressImage}
          disabled={!selectedFile || loading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
        >
          {loading ? 'Comprimindo...' : 'Comprimir Imagem'}
        </button>

        {compressedImage && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900">Imagem Comprimida</span>
              <button
                onClick={() => window.open(compressedImage.url, '_blank')}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <Download size={16} />
              </button>
            </div>
            <img
              src={compressedImage.url}
              alt="Comprimida"
              className="w-full h-auto rounded-lg"
            />
            <div className="mt-2 text-sm text-gray-600">
              Tamanho original: {compressedImage.originalSize} | 
              Tamanho comprimido: {compressedImage.compressedSize} | 
              Redução: {compressedImage.reduction}%
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Gerador de Thumbnail
const ThumbnailGenerator = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [thumbnailSize, setThumbnailSize] = useState(150);
  const [thumbnail, setThumbnail] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef();

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setThumbnail(null);
    } else {
      toast.error('Selecione um arquivo de imagem válido');
    }
  };

  const generateThumbnail = async () => {
    if (!selectedFile) {
      toast.error('Selecione uma imagem');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('size', thumbnailSize);

      const data = await apiUpload('/api/images/thumbnail', formData);
      setThumbnail(data);
    } catch (error) {
      toast.error('Erro ao gerar thumbnail');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Selecionar Imagem
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current.click()}
              className="flex items-center justify-center space-x-2 mx-auto bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <Upload size={16} />
              <span>Escolher Arquivo</span>
            </button>
            {selectedFile && (
              <div className="mt-4 text-sm text-gray-600">
                {selectedFile.name}
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Tamanho do Thumbnail: {thumbnailSize}x{thumbnailSize}px
          </label>
          <input
            type="range"
            min="50"
            max="300"
            value={thumbnailSize}
            onChange={(e) => setThumbnailSize(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        <button
          onClick={generateThumbnail}
          disabled={!selectedFile || loading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
        >
          {loading ? 'Gerando...' : 'Gerar Thumbnail'}
        </button>

        {thumbnail && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-900">Thumbnail Gerado</span>
              <button
                onClick={() => window.open(thumbnail.url, '_blank')}
                className="p-1 text-gray-500 hover:text-gray-700"
              >
                <Download size={16} />
              </button>
            </div>
            <img
              src={thumbnail.url}
              alt="Thumbnail"
              className="w-full h-auto rounded-lg"
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Analisador de Imagem
const ImageAnalyzer = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef();

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setAnalysis(null);
    } else {
      toast.error('Selecione um arquivo de imagem válido');
    }
  };

  const analyzeImage = async () => {
    if (!selectedFile) {
      toast.error('Selecione uma imagem');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);

      const data = await apiUpload('/api/images/analyze', formData);
      setAnalysis(data);
    } catch (error) {
      toast.error('Erro ao analisar imagem');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Selecionar Imagem
          </label>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current.click()}
              className="flex items-center justify-center space-x-2 mx-auto bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <Upload size={16} />
              <span>Escolher Arquivo</span>
            </button>
            {selectedFile && (
              <div className="mt-4 text-sm text-gray-600">
                {selectedFile.name}
              </div>
            )}
          </div>
        </div>

        <button
          onClick={analyzeImage}
          disabled={!selectedFile || loading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
        >
          {loading ? 'Analisando...' : 'Analisar Imagem'}
        </button>

        {analysis && (
          <div className="bg-gray-50 p-4 rounded-lg space-y-4">
            <h4 className="font-medium text-gray-900">Análise da Imagem</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-3 rounded-lg">
                <div className="text-sm text-gray-500">Dimensões</div>
                <div className="text-lg font-semibold">
                  {analysis.width} x {analysis.height}px
                </div>
              </div>
              
              <div className="bg-white p-3 rounded-lg">
                <div className="text-sm text-gray-500">Tamanho do Arquivo</div>
                <div className="text-lg font-semibold">
                  {analysis.fileSize}
                </div>
              </div>
              
              <div className="bg-white p-3 rounded-lg">
                <div className="text-sm text-gray-500">Formato</div>
                <div className="text-lg font-semibold">
                  {analysis.format}
                </div>
              </div>
              
              <div className="bg-white p-3 rounded-lg">
                <div className="text-sm text-gray-500">Tipo MIME</div>
                <div className="text-lg font-semibold">
                  {analysis.mimeType}
                </div>
              </div>
            </div>

            {analysis.colors && (
              <div>
                <div className="text-sm text-gray-500 mb-2">Cores Dominantes</div>
                <div className="flex space-x-2">
                  {analysis.colors.map((color, index) => (
                    <div
                      key={index}
                      className="w-8 h-8 rounded border border-gray-300"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Componente Gerador de QR Code
const QRCodeGenerator = () => {
  const [text, setText] = useState('');
  const [qrSize, setQrSize] = useState(200);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateQRCode = async () => {
    if (!text.trim()) {
      toast.error('Digite um texto ou URL');
      return;
    }

    setLoading(true);
    try {
      const data = await apiPost('/api/images/qr-generator', { text, size: qrSize });
      setQrCode(data);
    } catch (error) {
      toast.error('Erro ao gerar QR Code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Texto ou URL
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Digite o texto ou URL para gerar o QR Code..."
            className="w-full h-24 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Tamanho: {qrSize}x{qrSize}px
          </label>
          <input
            type="range"
            min="100"
            max="500"
            value={qrSize}
            onChange={(e) => setQrSize(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
        </div>

        <button
          onClick={generateQRCode}
          disabled={!text.trim() || loading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
        >
          {loading ? 'Gerando...' : 'Gerar QR Code'}
        </button>

        {qrCode && (
          <div className="bg-gray-50 p-4 rounded-lg text-center">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <span className="text-sm font-medium text-gray-900">QR Code Gerado</span>
              <button
                onClick={() => {
                  const a = document.createElement('a');
                  a.href = qrCode.dataUrl;
                  a.download = 'qrcode.png';
                  a.click();
                }}
                className="p-1 text-gray-500 hover:text-gray-700"
                title="Baixar QR Code"
              >
                <Download size={16} />
              </button>
            </div>
            <img
              src={qrCode.dataUrl}
              alt="QR Code"
              className="mx-auto rounded-lg"
              style={{ width: qrSize, height: qrSize }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Images; 